import { useEffect, useMemo, useState } from "react";
import {
  generatedApi,
  MissionControlTenant,
  PlannedShredProductGroup,
} from "src/store/api/generatedApi";
import { useAppDispatch } from "src/store/store";

interface TenantShredEntry {
  tenantName: string;
  shredFraction: number;
}

export interface ProductGroupRanking {
  productGroupName: string;
  tenants: TenantShredEntry[];
}

interface TenantProductGroups {
  shortName: string;
  productGroups: PlannedShredProductGroup[];
}

interface UseMissionControlShredRankingResult {
  isLoading: boolean;
  productGroupRankings: ProductGroupRanking[];
  availableMonths: string[];
  selectedMonthIndex: number;
  setSelectedMonthIndex: (index: number) => void;
}

const buildRankingsForMonth = (
  allTenantData: TenantProductGroups[],
  month: string
): ProductGroupRanking[] => {
  const byGroup = new Map<string, TenantShredEntry[]>();

  allTenantData.forEach(({ shortName, productGroups }) => {
    productGroups.forEach((pg) => {
      const monthEntry = pg.monthly_series.find((m) => m.month === month);
      if (!monthEntry) return;
      const entries = byGroup.get(pg.product_group_name) ?? [];
      entries.push({
        tenantName: shortName,
        shredFraction: monthEntry.shred_fraction,
      });
      byGroup.set(pg.product_group_name, entries);
    });
  });

  return [...byGroup.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([productGroupName, entries]) => ({
      productGroupName,
      tenants: entries.sort((a, b) => a.shredFraction - b.shredFraction),
    }));
};

export const useMissionControlPlannedShredRanking = (
  tenants: MissionControlTenant[]
): UseMissionControlShredRankingResult => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [allTenantData, setAllTenantData] = useState<TenantProductGroups[]>([]);
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);

  const tenantNamePairs = useMemo(
    () =>
      tenants.map((tenant) => ({
        tenant,
        primaryName: tenant.tenant_names[0] ?? "",
      })),
    [tenants]
  );

  useEffect(() => {
    let isMounted = true;

    const activePairs = tenantNamePairs.filter(
      (pair) => pair.primaryName.length > 0
    );

    const subscriptions = activePairs.map((pair) =>
      dispatch(
        generatedApi.endpoints.getTenantPlannedShred.initiate(
          { tenantName: pair.primaryName },
          { subscribe: true, forceRefetch: false }
        )
      )
    );

    const loadData = async () => {
      if (activePairs.length === 0) {
        if (!isMounted) return;
        setAllTenantData([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const responses = await Promise.all(
        subscriptions.map((sub) =>
          sub.unwrap().catch(() => ({
            product_groups: [] as PlannedShredProductGroup[],
          }))
        )
      );

      if (!isMounted) return;

      const tenantData: TenantProductGroups[] = activePairs.map((pair, i) => ({
        shortName: pair.tenant.short_name,
        productGroups: responses[i]?.product_groups ?? [],
      }));

      setAllTenantData(tenantData);
      setIsLoading(false);
    };

    void loadData();

    return () => {
      isMounted = false;
      subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    };
  }, [dispatch, tenantNamePairs]);

  const availableMonths = useMemo(() => {
    const monthSet = new Set<string>();
    allTenantData.forEach(({ productGroups }) => {
      productGroups.forEach((pg) => {
        pg.monthly_series.forEach((m) => monthSet.add(m.month));
      });
    });
    return [...monthSet].sort().reverse();
  }, [allTenantData]);

  const clampedMonthIndex = Math.min(
    selectedMonthIndex,
    Math.max(availableMonths.length - 1, 0)
  );

  const productGroupRankings = useMemo(() => {
    const month = availableMonths[clampedMonthIndex];
    if (!month) return [];
    return buildRankingsForMonth(allTenantData, month);
  }, [allTenantData, availableMonths, clampedMonthIndex]);

  return {
    isLoading,
    productGroupRankings,
    availableMonths,
    selectedMonthIndex: clampedMonthIndex,
    setSelectedMonthIndex,
  };
};
